import type { CardConfigs } from '@web-platform/shared-travel-tools-types/src/page-context';
import type { Translate } from '@web-platform/shared-travel-tools-types/src/translation-service';

export type AppCardProps = {
  category: string;
  categoryId: string;
  ctaLabel: string;
  description: string;
  href: string;
  id: string;
  name: string;
};

export const buildRoundTripCardProps = (translate: Translate): AppCardProps => ({
  id: 'round-trip',
  categoryId: 'car_hire',
  category: translate('travel_tools_card_round_trip_card_category'),
  name: translate('travel_tools_card_round_trip_name'),
  description: translate('travel_tools_card_round_trip_description'),
  ctaLabel: translate('travel_tools_card_round_trip_cta_label'),
  href: '/carhire?roadtrip=true',
});

export const buildCopyPasteCardProps = (translate: Translate): AppCardProps => ({
  id: 'copy-paste',
  categoryId: 'stays',
  category: translate('travel_tools_card_copy_paste_category'),
  name: translate('travel_tools_card_copy_paste_name'),
  description: translate('travel_tools_card_copy_paste_description'),
  ctaLabel: translate('travel_tools_card_copy_paste_cta_label'),
  href: '/hotels?copy-paste=true',
});

export const buildCabinBagGuideCardProps = (translate: Translate, href?: string): AppCardProps => ({
  id: 'cabin-bag-guide',
  categoryId: 'flights',
  category: translate('travel_tools_card_confidence_map_category'),
  name: translate('travel_tools_card_cabin_bag_guide_name'),
  description: translate('travel_tools_card_cabin_bag_guide_description'),
  ctaLabel: translate('travel_tools_card_cabin_bag_guide_cta_label'),
  href:
    href ||
    'https://www.skyscanner.net/flights/advice/cabin-luggage-guide-hand-baggage-sizes-and-weight-restrictions',
});

export const buildCarSeatMapCardProps = (translate: Translate, href?: string): AppCardProps => ({
  id: 'car-seat-map',
  categoryId: 'car_hire',
  category: translate('travel_tools_card_round_trip_card_category'),
  name: translate('travel_tools_card_car_seat_map_name'),
  description: translate('travel_tools_card_car_seat_map_description'),
  ctaLabel: translate('travel_tools_card_car_seat_map_cta_label'),
  href: href || 'https://www.skyscanner.net/carhire/advice/car-seat-map',
});

export const buildCheapestDestinationsCardProps = (
  translate: Translate,
  href?: string,
): AppCardProps => ({
  id: 'cheapest-destinations',
  categoryId: 'flights',
  category: translate('travel_tools_card_confidence_map_category'),
  name: translate('travel_tools_card_cheapest_destinations_name'),
  description: translate('travel_tools_card_cheapest_destinations_description'),
  ctaLabel: translate('travel_tools_card_cheapest_destinations_cta_label'),
  href: href || 'https://www.skyscanner.net/travel-trends/cheap',
});

export const buildEuropeanRoadTripsCardProps = (
  translate: Translate,
  href?: string,
): AppCardProps => ({
  id: 'european-road-trips',
  categoryId: 'car_hire',
  category: translate('travel_tools_card_round_trip_card_category'),
  name: translate('travel_tools_card_european_road_trips_name'),
  description: translate('travel_tools_card_european_road_trips_description'),
  ctaLabel: translate('travel_tools_card_european_road_trips_cta_label'),
  href:
    href ||
    'https://www.skyscanner.net/carhire/advice/the-best-drives-from-10-major-european-cities',
});

export const buildFlightTrackerCardProps = (translate: Translate, href?: string): AppCardProps => ({
  id: 'flight-tracker',
  categoryId: 'flights',
  category: translate('travel_tools_card_confidence_map_category'),
  name: translate('travel_tools_card_flight_tracker_name'),
  description: translate('travel_tools_card_flight_tracker_description'),
  ctaLabel: translate('travel_tools_card_flight_tracker_cta_label'),
  href: href || 'https://www.skyscanner.net/flight-tracker',
});

export const buildRunningMapCardProps = (translate: Translate, href?: string): AppCardProps => ({
  id: 'running-map',
  categoryId: 'flights',
  category: translate('travel_tools_card_confidence_map_category'),
  name: translate('travel_tools_card_running_map_name'),
  description: translate('travel_tools_card_running_map_description'),
  ctaLabel: translate('travel_tools_card_running_map_cta_label'),
  href: href || 'https://www.skyscanner.net/news/run-the-world',
});

export const buildSportscastCardProps = (translate: Translate, href?: string): AppCardProps => ({
  id: 'sportscast',
  categoryId: 'flights',
  category: translate('travel_tools_card_confidence_map_category'),
  name: translate('travel_tools_card_sportscast_name'),
  description: translate('travel_tools_card_sportscast_description'),
  ctaLabel: translate('travel_tools_card_sportscast_cta_label'),
  href: href || 'https://www.skyscanner.net/news/sportscast',
});

export const buildConfidenceMapCardProps = (translate: Translate): AppCardProps => ({
  id: 'travel-confidence-map',
  categoryId: 'flights',
  category: translate('travel_tools_card_confidence_map_category'),
  name: translate('travel_tools_card_confidence_map_name'),
  description: translate('travel_tools_card_confidence_map_description'),
  ctaLabel: translate('travel_tools_card_confidence_map_cta_label'),
  href: '/travel-tools/flight-price-map',
});

export const buildSkyMapCardProps = (): AppCardProps => ({
  id: 'sky-map',
  categoryId: 'flights',
  category: 'Flights',
  name: 'Sky Map',
  description: 'Explore flight routes and destinations on an interactive map',
  ctaLabel: 'Explore the map',
  href: '/travel-tools/sky-map',
});

type CardDefinition = {
  build: (translate: Translate, href?: string) => AppCardProps;
  configKey?: keyof CardConfigs;
  usesConfiguredHref?: boolean;
};

const cardDefinitions: CardDefinition[] = [
  { build: buildRoundTripCardProps },
  { build: buildCopyPasteCardProps },
  { build: buildCabinBagGuideCardProps, configKey: 'cabinBagGuide', usesConfiguredHref: true },
  { build: buildCarSeatMapCardProps, configKey: 'carSeatMap', usesConfiguredHref: true },
  {
    build: buildCheapestDestinationsCardProps,
    configKey: 'cheapestDestinations',
    usesConfiguredHref: true,
  },
  {
    build: buildEuropeanRoadTripsCardProps,
    configKey: 'europeanRoadTrips',
    usesConfiguredHref: true,
  },
  { build: buildFlightTrackerCardProps, configKey: 'flightTracker', usesConfiguredHref: true },
  { build: buildRunningMapCardProps, configKey: 'runningMap', usesConfiguredHref: true },
  { build: buildSportscastCardProps, configKey: 'sportscast', usesConfiguredHref: true },
  { build: buildConfidenceMapCardProps, configKey: 'confidenceMap' },
  { build: buildSkyMapCardProps, configKey: 'skyMap' },
];

export const buildAppCardProps = (translate: Translate, cardConfigs: CardConfigs): AppCardProps[] =>
  cardDefinitions.flatMap(({ build, configKey, usesConfiguredHref }) => {
    const config = configKey ? cardConfigs[configKey] : undefined;
    if (configKey && !config?.enabled) return [];

    return [build(translate, usesConfiguredHref ? config?.href : undefined)];
  });
